import { ganttStatus } from "./bookingStatus";
import { daysBetween } from "../../BookingGantt/utils/ganttLayout";

// The Booking Groups page's timeline view: the listed groups as bars by
// centre, with the independent students as one virtual row under each centre.
// Columns are nights (a bar covers startDate .. endDate - 1). Pure.

/** A date as "YYYY-MM-DD" from an ISO string, a [y, m, d] array or epoch ms; null otherwise. */
export function isoDay(v) {
  if (v === null || v === undefined || v === "") return null;
  if (Array.isArray(v) && v.length >= 3) {
    const [y, m, d] = v;
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }
  if (typeof v === "number" && Number.isFinite(v)) {
    const dt = new Date(v);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}
const key = (campus) => String(campus || "").trim().toLowerCase();

function addDays(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Independent students per centre from a /api/booking-gantt response:
 * { [campusKey]: { byDate: { date: count }, students: [...] } }. Seasons of
 * the same centre are merged.
 */
export function indiesByCampus(gantt) {
  const out = {};
  for (const c of gantt?.centres || []) {
    if (!c.indies) continue;
    const k = key(c.name);
    const entry = out[k] || (out[k] = { byDate: {}, students: [] });
    c.days.forEach((d, i) => {
      const n = c.indies.daily[i] || 0;
      if (n) entry.byDate[d.date] = (entry.byDate[d.date] || 0) + n;
    });
    entry.students.push(...c.indies.students);
  }
  return out;
}

/**
 * {
 *   dates: ["2027-06-27", ...],
 *   sections: [{ campus, bars: [{ group, offset, span, colorKey, cancelled, dateProblem }],
 *                indies: { daily: [...], peak, students } | null }],
 *   undated: [group, ...],
 * }
 * Sections keep the order centres first appear in (groups arrive sorted by start).
 * Only a group with no start date is left off. One with a start but no usable
 * end is drawn as a single night with dateProblem saying why, so it can be fixed.
 */
export function buildTimeline(groups, indies = {}) {
  const dated = [];
  const undated = [];
  for (const g of groups || []) {
    const s = isoDay(g.startDate);
    const e = isoDay(g.endDate);
    if (!s) undated.push(g);
    else if (!e) dated.push({ g, s, e: addDays(s, 1), dateProblem: "No end date" });
    else if (e <= s) dated.push({ g, s, e: addDays(s, 1), dateProblem: e === s ? "Ends the day it starts" : "Ends before it starts" });
    else dated.push({ g, s, e, dateProblem: null });
  }

  let start = null;
  let end = null; // last night
  const widen = (s, last) => {
    if (!start || s < start) start = s;
    if (!end || last > end) end = last;
  };
  for (const { s, e } of dated) widen(s, addDays(e, -1));

  // Indie nights only widen the axis for centres that have groups on screen.
  const campusKeys = new Set(dated.map(({ g }) => key(g.campus)));
  for (const k of campusKeys) {
    const nights = Object.keys(indies[k]?.byDate || {}).sort();
    if (nights.length && start) widen(nights[0], nights[nights.length - 1]);
  }
  if (!start) return { dates: [], sections: [], undated };

  const n = daysBetween(start, end) + 1;
  const dates = Array.from({ length: n }, (_, i) => addDays(start, i));

  const sections = [];
  const byCampus = {};
  for (const { g, s, e, dateProblem } of dated) {
    const k = key(g.campus);
    if (!byCampus[k]) {
      byCampus[k] = { campus: g.campus || "No campus", bars: [], indies: null };
      sections.push(byCampus[k]);
    }
    const colorKey = ganttStatus(g.status);
    byCampus[k].bars.push({
      group: g,
      offset: daysBetween(start, s),
      span: daysBetween(s, e),
      colorKey,
      cancelled: colorKey === null,
      dateProblem,
    });
  }

  for (const sec of sections) {
    const src = indies[key(sec.campus)];
    if (!src || !src.students.length) continue;
    const daily = dates.map((d) => src.byDate[d] || 0);
    const peak = Math.max(0, ...daily);
    if (peak) sec.indies = { daily, peak, students: src.students };
  }

  return { dates, sections, undated };
}

// ---- drawing helpers for the timeline view ----------------------------------

// One accent per centre, in the order the centres appear. Deliberately not the
// status colours: here colour says where, the dot says status.
const CENTRE_COLOURS = ["#3949ab", "#00897b", "#c2185b", "#6d4c41", "#5e35b1", "#0277bd", "#558b2f", "#ef6c00"];

export function centreColour(index) {
  return CENTRE_COLOURS[((index % CENTRE_COLOURS.length) + CENTRE_COLOURS.length) % CENTRE_COLOURS.length];
}

/** Sundays on the axis, as { date, index }: where the "w/c" labels and week bands go. */
export function weekStarts(dates) {
  const out = [];
  (dates || []).forEach((iso, index) => {
    if (new Date(`${iso}T00:00:00Z`).getUTCDay() === 0) out.push({ date: iso, index });
  });
  return out;
}

/** First to last night with anyone on site: { start, length } in axis columns. */
export function indieSpan(daily) {
  const first = daily.findIndex((n) => n > 0);
  if (first < 0) return { start: 0, length: 0 };
  let last = daily.length - 1;
  while (last > first && !daily[last]) last -= 1;
  return { start: first, length: last - first + 1 };
}

/**
 * SVG area path for a sparkline of nightly counts: one point in the middle of
 * each night, scaled so the peak reaches the top. Starts and ends on the baseline.
 */
export function sparklinePath(values, step, height, peak) {
  if (!values.length) return "";
  const top = Math.max(1, peak || Math.max(...values));
  const y = (v) => +(height - (Math.min(v, top) / top) * height).toFixed(1);
  const pts = values.map((v, i) => `L${+(i * step + step / 2).toFixed(1)},${y(v)}`);
  return `M0,${height} ${pts.join(" ")} L${values.length * step},${height} Z`;
}
